import { Permiso, TipoPermiso } from "../shared/model/permisos/permiso.model";

/**
 * Un permiso como lo enseña «Mi equipo» (29/10/2026). Norman: «al autorizar
 * un permiso salga bien, no en una ventana sino que salga como el de
 * vacaciones, y también permisos aparezcan en el calendario». En la bandeja
 * va con el mismo renglón de las vacaciones; en el calendario «Tu equipo»,
 * junto a ellas. Aquí se arma, una vez por permiso, lo que esos dos lugares
 * dicen de él.
 */

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

/** Qué es cada tipo, dicho como lo dice Norman («llegada tarde o salida
 *  temprano»), con el icono y la clase de color del calendario de permisos. */
const TIPOS: Record<TipoPermiso, { que: string; icono: string; clase: string }> = {
  FALTA: { que: "Falta", icono: "event_busy", clase: "es-falta" },
  SALIDA: { que: "Sale temprano", icono: "logout", clase: "es-salida" },
  ENTRADA: { que: "Llega tarde", icono: "login", clase: "es-entrada" },
};

export interface PermisoEnEquipo {
  permiso: Permiso;
  /** «Norman E.»: el nombre en la etiqueta del calendario, donde junto a la
   *  hora no cabe el entero (va en el globo). */
  corto: string;
  /** El día del permiso en ISO (aaaa-mm-dd): con él va a su celda. */
  fecha: string;
  /** «Llega tarde el viernes 30 de octubre»: el renglón de abajo del nombre. */
  que: string;
  /** Lo mismo con la hora, «… a las 10:00»: el permiso en una frase. */
  frase: string;
  /** La hora, sólo si no es el día entero: la de la etiqueta del calendario. */
  hora: string;
  /** «10:00», o el día completo si falta: la pastilla del renglón, donde
   *  las vacaciones dicen cuántos días son. */
  horas: string;
  icono: string;
  /** es-falta, es-salida o es-entrada: el color de la barrita del renglón. */
  clase: string;
  /** Lo que escribió quien lo pide. */
  nota: string;
  /** Todavía no se resuelve: en el calendario va punteado. */
  pendiente: boolean;
  /** El globo de su etiqueta en el calendario: quién, qué, cuándo y cómo va. */
  tip: string;
}

/** «viernes 30 de octubre», y «viernes 1 de enero de 2027» si no es de este año. */
function diaDe(fecha: string, anioActual: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha);
  if (!m) {
    return fecha;
  }
  // Con año, mes y día sueltos: un texto «aaaa-mm-dd» se leería en UTC y en
  // México caería en el día anterior.
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const anio = d.getFullYear() === anioActual ? "" : ` de ${d.getFullYear()}`;
  return `${DIAS[d.getDay()]} ${d.getDate()} de ${MESES[d.getMonth()]}${anio}`;
}

/**
 * El nombre y la inicial del apellido paterno: «Norman E.». Con nombre
 * compuesto el paterno es la tercera palabra y con nombre simple la
 * segunda, la misma regla de las iniciales del calendario de permisos.
 */
function nombreCorto(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length < 2) {
    return partes[0] || "";
  }
  const paterno = partes.length >= 4 ? partes[2] : partes[1];
  return `${partes[0]} ${paterno.charAt(0)}.`;
}

export function permisoEnEquipo(p: Permiso, anioActual: number): PermisoEnEquipo {
  const tipo = TIPOS[p.tipo || "FALTA"] || TIPOS.FALTA;
  const fecha = (p.fecha || "").substring(0, 10);
  // Faltar es el día completo: si por algo trajera hora, no se enseña.
  const hora = tipo === TIPOS.FALTA ? "" : p.hora || "";
  const pendiente = p.estatus === "PENDIENTE";
  const que = `${tipo.que} el ${diaDe(fecha, anioActual)}`;
  const frase = hora ? `${que} a las ${hora}` : que;
  return {
    permiso: p,
    corto: nombreCorto(p.colaborador || ""),
    fecha,
    que,
    frase,
    hora,
    horas: hora || "Todo el día",
    icono: tipo.icono,
    clase: tipo.clase,
    nota: (p.observaciones || "").trim(),
    pendiente,
    tip: `${p.colaborador || ""} · ${frase} · ${pendiente ? "esperando tu respuesta" : "autorizado"}`,
  };
}
